import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham-dark.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Paper, Grid } from '@material-ui/core';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import store from '../../../redux/store';
import { getPricingStrategyTagNameFromId } from '../../../helpers/pricing';
import GroupRow from './complementaries/GroupRow';
import CustomTableCell from './complementaries/CustomTableCell';
import { setGroupsOpenned } from '../../../redux/actions/refresh-groups-inventory';

const useStyles = makeStyles(theme => ({
  headerCellStyles: {
    padding: '10px !important',
  },
  headerGroupNametCellStyles: {
    padding: '10px 10px 10px 1px !important',
  },
  typographyStyle: {
    color: theme.palette.type === 'light' ? '#707171' : '#E9E9E9',
  },
  iconStyles: {
    color: theme.palette.type === 'light' ? '#45505E' : '#E9E9E9',
    '&:hover': {
      color: '#A049AF',
    },
  },
  headerFirstCellStyles: {
    padding: '0px 0px 0px 10px !important',
  },
  maxMinButtonsStyle: {
    border: '1px solid #D9DDDF',
    '&:hover': {
      border: '1px solid #D9DDDF',
      backgroundColor: '#E7DBE9',
    },
  },
}));

const columnNames = ['Group Name', 'Floor', 'Ceiling', 'Increment', 'Spaceout', 'Anchor', 'Tickets', 'Listings'];

const GroupElement = props => {
  const {
    allGroups,
    filteredStubhubListings,
    focusedPricingGroup,
    getAnchorListingPrice,
    groupedListings,
    handleOnDragEnd,
    isAbTest,
    isCompAllGroup,
    isDragging,
    onBeforeCapture,
    selectedIndex,
    setSelectedIndex,
    setUpdateListingsOnSave,
    settings,
    tags,
    typeListing,
    groupsOpenned,
    marketplaces,
    handleGroupAttrEdit,
  } = props;

  const theme = useTheme();
  const classes = useStyles();

  const handleCloseGroups = () => {
    store.dispatch(setGroupsOpenned([]));
  };

  const handleOpenGroups = () => {
    const allGroupsId = allGroups.filter(item => !groupsOpenned.includes(item.id)).map(item => item.id);
    const newGroupsOppened = allGroupsId.concat(groupsOpenned);
    store.dispatch(setGroupsOpenned(newGroupsOppened.reverse()));
  };

  const calculateTickets = item => {
    let tickets = 0;
    item.ranks.forEach(rank => {
      const groups = groupedListings.filter(gl => gl.skybox_id === rank.skybox_listing_id);
      groups.forEach(g => {
        tickets += g.quantity;
      });
    });
    return tickets;
  };

  const isOpenGroup = rowInfoId => {
    if (groupsOpenned.includes(rowInfoId)) {
      return true;
    }
    return false;
  };

  const createData = () => {
    return allGroups.map(item => {
      const skyboxIds = item?.ranks?.map(rank => rank.skybox_listing_id) || [];
      const broadcasted = groupedListings.filter(gl => skyboxIds.includes(gl.skybox_id) && Boolean(gl.broadcast));
      const tickets = calculateTickets(item);

      return {
        name: item.name,
        floor: `$${item.floor || ''}`,
        ceiling: item.ceiling ? `$${item.ceiling}` : '',
        increment: item.increment,
        increment_type: item.increment_type,
        spaceout: item.spaceout,
        spaceout_type: item.spaceout_type,
        anchor: getAnchorListingPrice(item),
        quantity: tickets,
        listing: `${broadcasted.length}/${item?.ranks?.length}`,
        PricingStrategy: `${(item.pricing_strategy_id &&
          getPricingStrategyTagNameFromId(tags, item.pricing_strategy_id)) ||
          'None'}`,
        id: item.id,
        last_sold_date: item.last_sold_date,
        last_date_change: item.last_date_change,
        last_user_change: item.last_user_change,
        last_api_change: item.last_api_change,
        isActive: item.id === focusedPricingGroup,
        internal_notes_validation: item.internal_notes_validation || '',
        marketplace: item.marketplace,
        ranks: item.ranks,
        has_dynamic_spaceout: item.has_dynamic_spaceout,
        dynamic_spaceout_enabled: item.dynamic_spaceout_enabled,
        manual_price: item.manual_price,
        splits: item.splits,
        auto_broadcast: item.auto_broadcast,
        comp_all: item.comp_all,
        comparison_has_listing: item.comparison_has_listing,
        custom_split: item.custom_split,
        sg_sections: item.sg_sections,
        tnd_sections: item.tnd_sections,
        total_quantity: item.total_quantity,
        discount: item.discount,
        sg_discount: item.sg_discount,
        exclude_obstructed_view: item.exclude_obstructed_view,
        isOpen: isOpenGroup(item.id),
        pricing_strategy_id: item.pricing_strategy_id ?? -1,
        pricing_strategy_comments: item.pricing_strategy_comments ?? '',
        tmr_unique_pricing: item?.unique_pricing?.TicketsNow,
        sh_unique_pricing: item?.unique_pricing?.StubHub,
        vs_unique_pricing: item?.unique_pricing?.Vivid,
      };
    });
  };

  const [dataRows, setDataRows] = useState(createData());

  useEffect(() => {
    if (allGroups) {
      const newData = createData();
      setDataRows(newData);
    }
  }, [JSON.stringify(allGroups)]);

  return (
    <div>
      <TableContainer component={Paper} style={{ marginTop: '20px', borderRadius: '0px' }}>
        <DragDropContext onDragEnd={handleOnDragEnd} onBeforeCapture={onBeforeCapture}>
          <Table aria-label="collapsible table">
            <TableHead
              style={{
                backgroundColor: theme.palette.type === 'light' ? '#F5F7F7' : '#1C1C1C',
              }}
            >
              <TableRow>
                <CustomTableCell className={classes.headerFirstCellStyles}>
                  <Grid container direction="row">
                    <Grid item className={classes.maxMinButtonsStyle}>
                      <IconButton onClick={handleOpenGroups} style={{ padding: '0px' }}>
                        <AddIcon className={classes.iconStyles} />
                      </IconButton>
                    </Grid>
                    <Grid item className={classes.maxMinButtonsStyle}>
                      <IconButton onClick={handleCloseGroups} style={{ padding: '0px' }}>
                        <RemoveIcon className={classes.iconStyles} />
                      </IconButton>
                    </Grid>
                  </Grid>
                </CustomTableCell>
                {columnNames.map((name, index) => (
                  <CustomTableCell
                    className={index === 0 ? classes.headerGroupNametCellStyles : classes.headerCellStyles}
                    typographyClassName={classes.typographyStyle}
                  >
                    <Grid container direction="row" justifyContent="space-between">
                      <Grid item>{name}</Grid>
                      {index === columnNames.length - 1 ? (
                        <></>
                      ) : (
                        <Grid item>
                          <Divider flexitem orientation="vertical" />
                        </Grid>
                      )}
                    </Grid>
                  </CustomTableCell>
                ))}
              </TableRow>
            </TableHead>
            <Droppable droppableId="groups" type="ROWS">
              {provided => (
                <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                  {dataRows.map((row, index) => (
                    <GroupRow
                      filteredStubhubListings={filteredStubhubListings}
                      focusedPricingGroup={focusedPricingGroup}
                      groupedListings={groupedListings}
                      index={index}
                      isAbTest={isAbTest}
                      isCompAllGroup={isCompAllGroup}
                      isDragging={isDragging}
                      key={row.id}
                      id={row.id}
                      rowInfo={row}
                      selectedIndex={selectedIndex}
                      setSelectedIndex={setSelectedIndex}
                      setUpdateListingsOnSave={setUpdateListingsOnSave}
                      settings={settings}
                      typeListing={typeListing}
                      allGroupsLength={allGroups.length}
                      groupsOpenned={groupsOpenned}
                      marketplaces={marketplaces}
                      handleGroupAttrEdit={handleGroupAttrEdit}
                    />
                  ))}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </Table>
        </DragDropContext>
      </TableContainer>
    </div>
  );
};

GroupElement.defaultProps = {
  focusedPricingGroup: null,
  marketplaces: null,
};

GroupElement.propTypes = {
  allGroups: PropTypes.array.isRequired,
  filteredStubhubListings: PropTypes.array.isRequired,
  focusedPricingGroup: PropTypes.number,
  getAnchorListingPrice: PropTypes.func.isRequired,
  groupedListings: PropTypes.array.isRequired,
  groupsUpdateReArrange: PropTypes.func.isRequired,
  handleOnDragEnd: PropTypes.func.isRequired,
  isAbTest: PropTypes.bool.isRequired,
  isCompAllGroup: PropTypes.bool.isRequired,
  isDragging: PropTypes.bool.isRequired,
  onBeforeCapture: PropTypes.func.isRequired,
  selectedIndex: PropTypes.object.isRequired,
  setSelectedIndex: PropTypes.func.isRequired,
  setUpdateListingsOnSave: PropTypes.func.isRequired,
  settings: PropTypes.object.isRequired,
  tags: PropTypes.array.isRequired,
  typeListing: PropTypes.string.isRequired,
  groupsOpenned: PropTypes.array.isRequired,
  marketplaces: PropTypes.object,
  handleGroupAttrEdit: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    groupsOpenned: state.refreshGroupsInventory.groupsOpenned,
  };
}

export default connect(mapStateToProps)(GroupElement);
